import { Container, LoadingScreen } from '@/shared/components'
import { Button, H2, BreadcrumbsBar } from '@/common'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useSession } from '@/shared/hooks/useSession'
import { useGetDeclarantById, useUpdateDeclarant } from '@/features/cbam-reporting/hooks/declarants'
import useI18n from '@/shared/hooks/usei18n'
import toast from 'react-hot-toast'
import DeclarantForm from '../../components/DeclarantForm'
import {
  DeclarantFormData,
  validateDeclarantUpdateForm
} from '../../components/DeclarantForm.schema'

export default function DeclarantEditPage() {
  const { t } = useI18n()
  const { declarantId } = useParams<{ declarantId: string }>()
  const organizationId = useSession().session.organization.id

  const declarantQuery = useGetDeclarantById(organizationId, declarantId!)
  const { mutateAsync: updateDeclarant, isPending: isUpdating } = useUpdateDeclarant(
    organizationId,
    declarantId!
  )

  const declarant = declarantQuery.data
  const isLoading = declarantQuery.isLoading

  const [formData, setFormData] = useState<DeclarantFormData>({
    name: '',
    type: undefined,
    identificationNumber: '',
    country: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    subDivision: ''
  })

  const [isInitialized, setIsInitialized] = useState(false)
  const [isFormPristine, setIsFormPristine] = useState(true)

  useEffect(() => {
    if (declarant && !isInitialized) {
      setFormData({
        name: declarant.name || '',
        type: declarant.type || undefined,
        identificationNumber: declarant.identificationNumber || '',
        country: declarant.country || '',
        street: declarant.street || '',
        streetNumber: declarant.streetNumber || '',
        street2: declarant.street2 || '',
        postCode: declarant.postCode || '',
        city: declarant.city || '',
        poBox: declarant.poBox || '',
        subDivision: declarant.subDivision || ''
      })
      setIsInitialized(true)
      setIsFormPristine(true)
    }
  }, [declarant, isInitialized])

  const onFormChange = useCallback((data: DeclarantFormData) => {
    setFormData(data)
    setIsFormPristine(false)
  }, [])

  const validationResult = useMemo(() => {
    return validateDeclarantUpdateForm({
      name: formData.name,
      identificationNumber: formData.identificationNumber,
      street: formData.street,
      streetNumber: formData.streetNumber,
      street2: formData.street2,
      postCode: formData.postCode,
      city: formData.city,
      poBox: formData.poBox,
      subDivision: formData.subDivision
    })
  }, [formData])

  // Sync identification number from declarant query (after dialog update)
  useEffect(() => {
    if (declarant && isInitialized) {
      setFormData(prev => ({
        ...prev,
        identificationNumber: declarant.identificationNumber || ''
      }))
    }
  }, [declarant, isInitialized])

  const isSaveDisabled = !validationResult.success || isFormPristine

  const onSave = useCallback(async () => {
    if (!validationResult.success) {
      return
    }

    try {
      await updateDeclarant({
        name: formData.name,
        identificationNumber: formData.identificationNumber || undefined,
        street: formData.street || undefined,
        streetNumber: formData.streetNumber || undefined,
        street2: formData.street2 || undefined,
        postCode: formData.postCode || undefined,
        city: formData.city || undefined,
        poBox: formData.poBox || undefined,
        subDivision: formData.subDivision || undefined
      })

      toast.success(t('declarants:edit.notifications.success'))
      setIsFormPristine(true)
    } catch (error) {
      console.error('Error updating declarant:', error)
      toast.error(t('declarants:edit.notifications.error'))
    }
  }, [validationResult, formData, updateDeclarant, t])

  const onRestore = useCallback(() => {
    if (declarant) {
      setFormData({
        name: declarant.name || '',
        type: declarant.type || undefined,
        identificationNumber: declarant.identificationNumber || '',
        country: declarant.country || '',
        street: declarant.street || '',
        streetNumber: declarant.streetNumber || '',
        street2: declarant.street2 || '',
        postCode: declarant.postCode || '',
        city: declarant.city || '',
        poBox: declarant.poBox || '',
        subDivision: declarant.subDivision || ''
      })
      setIsFormPristine(true)
    }
  }, [declarant])

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!declarant) {
    return <Navigate to="/cbam-reporting" />
  }

  const breadcrumbs = [
    { label: t('common:breadcrumbs.cbamReporting'), to: `/cbam-reporting/${declarantId}` },
    { label: t('declarants:edit.title'), to: '' }
  ]

  return (
    <div className="flex flex-col h-full">
      <BreadcrumbsBar links={breadcrumbs} />
      <Container>
        <header>
          <H2>{t('declarants:edit.title')}</H2>
        </header>
        <DeclarantForm data={formData} onChange={onFormChange} isEditMode={true} />
      </Container>
      <footer className="flex justify-end gap-2 mt-auto bg-white p-2.5 border-y">
        <Button disabled={isFormPristine} variant="subtle" onClick={onRestore}>
          {t('common:actions.restore')}
        </Button>
        <Button className="w-48" onClick={onSave} isLoading={isUpdating} disabled={isSaveDisabled}>
          {t('common:actions.save')}
        </Button>
      </footer>
    </div>
  )
}
